import { graphql } from "gatsby";
import React from "react";
import Seo from "../components/common/SEO";
import { GatsbyImage } from "gatsby-plugin-image";
import FaqSection from "../components/home/faqSection";
import { PortableText } from "@portabletext/react";

const Locationpage = ({ data }) => {
  const locationdata = data.sanityLocation;
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-white">
      {/* Hero Section */}
      <div className="relative lg:h-[50vh]">
        <GatsbyImage
          image={locationdata.shopImage.asset.gatsbyImageData}
          alt={locationdata.shopImage.alt || locationdata.title}
          objectFit="cover"
          priority
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center drop-shadow-lg">
            {locationdata.title}
          </h1>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-4 py-5">
        <div className="grid sm:grid-cols-2 gap-8">
          {/* Left Column: Address and Hours */}
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6 text-primary"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                />
              </svg>

              <div>
                <h2 className="text-xl font-semibold mb-2">Visit Us</h2>
                <p className="text-gray-600 whitespace-pre-wrap">
                  {locationdata.shopLocation}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6 text-primary"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>

              <div>
                <h2 className="text-xl font-semibold mb-2">Opening Hours</h2>
                <p className="text-gray-600">{locationdata.shopTimings}</p>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Info and CTA */}
          <div className="space-y-6">
            {locationdata.phoneNo && (
              <div className="flex items-start space-x-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6 text-primary"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                  />
                </svg>
                <div>
                  <h2 className="text-xl font-semibold mb-2">Call Us</h2>
                  <p className="text-gray-600">{locationdata.phoneNo}</p>
                </div>
              </div>
            )}
            {locationdata.email && (
              <div className="flex items-start space-x-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6 text-primary"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                  />
                </svg>
                <div>
                  <h2 className="text-xl font-semibold mb-2">Email Us</h2>
                  <p className="text-gray-600">{locationdata.email}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Map Section */}
      <div className="mt-5 w-full">
        <iframe
          src={locationdata.locationUrl}
          className="w-full min-h-screen"
        />
      </div>
      {locationdata._rawDescription && (
        <div className="w-full px-5 sm:px-10 py-5 sm:py-10">
          <div className="max-w-7xl w-full mx-auto text-center text-lg sm:text-xl">
            <PortableText value={locationdata._rawDescription} />
          </div>
        </div>
      )}
      <FaqSection data={locationdata.faqSection} />
    </div>
  );
};

export function Head({ location, data }) {
  return <Seo location={location} data={data?.sanityLocation?.seo} />;
}

export const query = graphql`
  query ($slug: String) {
    sanityLocation(slug: { current: { eq: $slug } }) {
      seo {
        title
        description
        keywords
      }
      title
      shopImage {
        alt
        asset {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            formats: [WEBP, AUTO]
          )
        }
      }
      shopLocation
      shopTimings
      phoneNo
      email
      locationUrl
      _rawDescription
      faqSection {
        sectionId
        title
        faqs {
          question
          _rawAnswer
        }
      }
    }
  }
`;

export default Locationpage;
